import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Textarea,
  Avatar,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { Camera, ChevronRight, SkipForward } from 'lucide-react';
import { SEO } from '@/components/seo';
import { useGetCurrentUser } from '@/api/queries/useUserQueries';
import { useUpdateUser } from '@/api/mutations/useUserMutations';
import { useCompleteProfile } from '@/api/mutations/useOnboardingMutations';

/**
 * OnboardingProfileSetup page - Profile setup during onboarding
 *
 * Allows users to:
 * - Upload a profile photo
 * - Write a bio
 *
 * This step is optional and can be skipped.
 * Navigates to Find Friends page after completion.
 */
export function OnboardingProfileSetup() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: user } = useGetCurrentUser();
  const { mutate: updateUser, isPending } = useUpdateUser();
  const { mutate: completeProfile } = useCompleteProfile();

  const [bio, setBio] = useState(user?.bio || '');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const navigateToNextStep = () => {
    // Mark profile step as complete and navigate to friends page
    completeProfile(undefined, {
      onSettled: () => {
        navigate({ to: '/onboarding/friends' });
      },
    });
  };

  const handleSave = () => {
    updateUser(
      { bio },
      {
        onSuccess: () => {
          toast({
            title: 'Profile updated',
            status: 'success',
            duration: 2000,
          });
          navigateToNextStep();
        },
        onError: (error: any) => {
          toast({
            title: 'Failed to update profile',
            description: error?.message || 'Please try again',
            status: 'error',
            duration: 3000,
          });
        },
      }
    );
  };

  const handleSkip = () => {
    // Mark profile step as complete even when skipping
    navigateToNextStep();
  };

  return (
    <Box
      minH="100vh"
      color="white"
      display="flex"
      flexDirection="column"
    >
      <SEO title="Set Up Your Profile" />

      {/* Skip button — pt includes safe-area inset for Dynamic Island / notch */}
      <Flex
        justify="flex-end"
        px={4}
        pb={4}
        pt="calc(16px + env(safe-area-inset-top))"
      >
        <Button
          variant="ghost"
          color="var(--canvas-text-2)"
          rightIcon={<SkipForward size={16} />}
          onClick={handleSkip}
          _hover={{ color: 'var(--canvas-text)' }}
          data-testid="profile-skip"
        >
          Skip
        </Button>
      </Flex>

      <Flex
        direction="column"
        align="center"
        justify="center"
        flex="1"
        p={8}
        textAlign="center"
      >
        <VStack spacing={6} maxW="400px" width="100%">
          {/* Header */}
          <VStack spacing={2}>
            <Heading size="lg" color="white">
              Set Up Your Profile
            </Heading>
            <Text fontSize="md" color="var(--canvas-text-2)">
              Help others recognize you
            </Text>
          </VStack>

          {/* Profile Photo */}
          <Box position="relative">
            <Avatar
              size="2xl"
              src={photoPreview || user?.photoUrl || undefined}
              name={user?.name || undefined}
              bg="purple.500"
              // TEXT-COLOR-LITERAL: deliberate fix for Chakra's random-color initials-fallback contrast bug, pinned against fixed purple.500 bg (non-mode-paired, ui-ux-designer reviewed)
              color="white"
            />
            <IconButton
              aria-label="Upload photo"
              icon={<Camera size={20} />}
              size="sm"
              borderRadius="full"
              position="absolute"
              bottom={0}
              right={0}
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={() => document.getElementById('photo-input')?.click()}
            />
            <input
              id="photo-input"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
              data-testid="profile-photo-input"
            />
          </Box>

          {/* Bio */}
          <VStack width="100%" align="stretch" spacing={2}>
            <Text fontSize="sm" color="var(--canvas-text-2)" textAlign="left">
              Bio (optional)
            </Text>
            <Textarea
              placeholder="Tell others a little about yourself and your food preferences..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={255}
              rows={4}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              _focus={{
                borderColor: 'purple.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
              }}
              data-testid="profile-bio-input"
            />
            <Text fontSize="xs" color="var(--canvas-text-2)" textAlign="right" data-testid="profile-bio-counter">
              {bio.length}/255
            </Text>
          </VStack>

          {/* Action Buttons */}
          <VStack width="100%" spacing={3} mt={4}>
            <Button
              size="lg"
              width="100%"
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleSave}
              isLoading={isPending}
              rightIcon={<ChevronRight size={20} />}
              data-testid="profile-continue"
            >
              Continue
            </Button>
          </VStack>
        </VStack>
      </Flex>
    </Box>
  );
}

export default OnboardingProfileSetup;
